/**
 * Feed Management Module
 *
 * This module is responsible for handling the creation, retrieval, update,
 * and deletion of feed items within a calendar-based application. It offers
 * a set of API methods to interact with server-side feed endpoints and manages
 * the feed data within the client-side application context.
 *
 * @module aimi_feed
 * @param {Object} aimi_feed - The main object for the feed module, used as a namespace.
 * @param {Object} $ - The jQuery object, for DOM manipulation and AJAX requests.
 * @param {Object} app - The main application object, providing access to shared functionalities and API request methods.
 * @param {undefined} [undefined] - An explicit undefined parameter for safety checks.
 */
(function (aimi_feed, $, app, undefined) {
  // Private object to maintain internal state
  let __this = {};
  __this.feeds = {};  // Stores feed items indexed by their IDs
  __this.$body = $('body');  // Cached jQuery selector for the document body

  /**
   * Defines API methods related to feed operations. These methods include
   * fetching system feeds, specific feed items, updating, adding, and deleting feeds.
   */
  app.apis['feed'] = function () {
    return {
      'fetchSystemFeeds': function () {
        return app.getRequest(`/api/calendar/${__this.calendarId}/system-feeds`, {},
            function () {
            },
            function () {
            }
        );
      },
      'fetch': function () {
        return app.getRequest(`/api/calendar/${__this.calendarId}/feeds`, {},
            function () {
            },
            function () {
            }
        );
      },

      update: function (id, requestData) {
        return app.putRequest(`/api/calendar/${__this.calendarId}/feed/${id}`, requestData,
            function () {
            },
            function () {
            }
        );
      },

      addLibraryFeed: function (requestData) {
        return app.putRequest(`/api/calendar/${__this.calendarId}/feeds`, requestData,
            function () {
            },
            function () {
            }
        );
      },

      addCustomFeed: function (requestData) {
        return app.postRequest(`/api/calendar/${__this.calendarId}/feeds`, requestData,
            function () {
            },
            function () {
            }
        );
      },

      deleteFeed: function (id) {
        return app.deleteRequest(`/api/calendar/${__this.calendarId}/feed/${id}`, {},
            function () {
            },
            function () {
            }
        );
      },
      'articleSwap': function (postId, articleId) {
        const requestData = {'postId': postId, 'articleId': articleId};
        return app.postRequest(`/api/calendar/${__this.calendarId}/article/swap`, requestData,
            function () {
            },
            function () {
            }
        );
      },
      saveFeedSettings: function (requestData) {
        return app.putRequest(`/api/calendar/${__this.calendarId}/feed-settings`, requestData,
            function () {
            },
            function () {
            }
        );
      },
      getFeedSettings: function (requestData) {
        return app.getRequest(`/api/calendar/${__this.calendarId}/feed-settings`, {},
            function () {
            },
            function () {
            }
        );
      }
    }
  }();

  /**
   * The view component responsible for UI interactions and visual feedback.
   */
  aimi_feed.view = {
    init: function () {
    }
  }

  aimi_feed.saveFeedSettingsCallback = function (response) {
    app.notifyEl.update({'message': response.message, progress: 90});
    aimi_feed.setting.refresh(__this.calendarId).then(function (response) {

    });
  }

  // e.g {
  //   "timeFrames": {
  //     "custom": {
  //       "days": [
  //         "Tuesday",
  //         "Wednesday",
  //         "Thursday"
  //       ],
  //       "time": [
  //         "6:00 - 9:00 AM",
  //         "9:00 - 12:00 PM",
  //         "12:00 - 3:00 PM",
  //         "3:00 - 5:00 PM",
  //         "5:00 - 8:00 PM"
  //       ]
  //     }
  //   },
  //   "frequency": {
  //     "postTimes": "2"
  //   },
  //   "mdcId": "test",
  //   "chronicPost": true,
  //   "appendDescription": true
  // }
  aimi_feed.payloadFeedSettings = function (requestData) {
    return {
      'timeFrames': {
        'custom': {
          'days': requestData.days,
          'time': app.removeEmptyValues(requestData.time),
        }
      },
      frequency: {
        postTimes: requestData["frequency.postTimes"],
      },
      mdcId: requestData.mdcId,
      chronicPost: requestData.appendArticleRandomlyChronically === 'chronic',
      appendDescription: requestData.appendArticleDescription === 'on'
    }
  }
  /**
   * Aimi Feed Setting Object.
   * @type {Object}
   * @property {string[]} timeFrames - An array containing different time frames.
   * @property {string[]} days - An array containing names of the days in a week.
   * @property {Function} renderTimeFrames - A function to render time frames dynamically.
   * @property {Function} renderDays - A function to render days dynamically.
   * @property {Function} refresh - A function to refresh feed settings asynchronously.
   * @property {Function} init - A function to initialize feed settings.
   */
  aimi_feed.setting = {
    showLoader: function () {
      __this.$form.addClass('m-progress');
    },
    removeLoader: function () {
      __this.$form.removeClass('m-progress');
    },
    /**
     * Asynchronously refreshes feed settings.
     * @async
     * @param {string} calendarId - The ID of the calendar.
     * @returns {Promise} The promise containing the response.
     */
    refresh: async function (calendarId) {
      let __view = this;
      let response = await app.apis['feed'].getFeedSettings(calendarId);
      __this.feedSettings = response.data.config;
      return __this.feedSettings;
    },
    /**
     * Array containing various time frames.
     * @type {string[]}
     */
    timeFrames: [
      'Anytime',
      '6:00 - 9:00 AM',
      '9:00 - 12:00 PM',
      '12:00 - 3:00 PM',
      '3:00 - 5:00 PM',
      '5:00 - 8:00 PM',
      '8:00 - 11:00 PM',
      '11:00 - 6:00 AM',
    ],
    /**
     * Array containing names of the days in a week.
     * @type {string[]}
     */
    days: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    /**
     * Renders time frames dynamically.
     * @function
     */
    renderTimeFrames: function () {
      const $container = $('[data-timeframes]');
      const page = _.template(app.templates['feed__setting-row-timeframe']);
      $.each(this.timeFrames, function (index, timeframe) {
        const $page = $(page({
          'id': `timeframe-${index}`,
          name: 'time[]',
          'required': true,
          'label': timeframe,
          'color': 'primary'
        }))
        $page.appendTo($container);
      });
    },
    /**
     * Renders days dynamically.
     * @function
     */
    renderDays: function () {
      const $container = $('[data-days]');
      const page = _.template(app.templates['feed__setting-row-timeframe']);
      $.each(this.days, function (index, day) {
        const $page = $(page({
          'id': `days-${index}`,
          name: 'days[]',
          'required': true,
          'label': day,
          'color': 'primary'
        }))
        $page.appendTo($container);
      });
    },

    /**
     * Initializes the feed settings.
     * @param {string} calendarId - The ID of the calendar.
     */
    init: function (calendarId) {
      // Assigns the reference of 'this' to 'self'
      let self = this;

      // Sets the calendar ID with the provided value
      __this.calendarId = calendarId;
      __this.$form = $('form[name=aimi-feed-setting]');
      // Selects the custom time input field using jQuery
      __this.fcCustomTime = $(':input#customTimeInput');
      // Selects the custom time button using jQuery
      __this.btnCustomTime = $('#customTime');
      __this.switchCustomTime = $('#switchCustomTime');
      __this.inputFrequencyPostTimes = $('input[name="frequency.postTimes"]', __this.$form);
      __this.switchInputMdcId = $('#switchTrackingId', __this.$form);
      __this.inputMdcId = $('input[name="mdcId"]', __this.$form);
      __this.switchChronic = $('#append-article-chronically', __this.$form);
      __this.switchRandomly = $('#append-article-randomly', __this.$form);
      __this.switchAppendArticleDescription = $('#switchAppendArticleDescription', __this.$form);

      // Renders time frames
      self.renderTimeFrames();

      // Renders days
      self.renderDays();

      // Document Ready
      __this.inputDays = $('input[name="days[]"]', __this.$form);
      __this.inputTime = $('input[name="time[]"]', __this.$form);
      self.showLoader();
      // Asynchronously refreshes feed settings and handles the response
      this.refresh(__this.calendarId).then(function (response) {
        self.setValues(response)
        self.removeLoader();
      });

      // Initializes a new instance of Tempus Dominus for custom time picking
      __this.customTimePicker = new tempusDominus.TempusDominus(__this.btnCustomTime[0], {
        // Configuration for the display of the time picker
        display: {
          viewMode: 'clock',
          // Icons used in the time picker
          icons: {
            type: 'icons',
            time: 'fa fa-solid fa-clock',
            date: 'fa fa-solid fa-calendar',
            up: 'fa fa-solid fa-arrow-up',
            down: 'fa fa-solid fa-arrow-down',
            previous: 'fa fa-solid fa-chevron-left',
            next: 'fa fa-solid fa-chevron-right',
            today: 'fa fa-solid fa-calendar-check',
            clear: 'fa fa-solid fa-trash',
            close: 'fa fa-solid fa-xmark'
          },
          // Components of the time picker to display
          components: {
            decades: false,
            year: false,
            month: false,
            date: false,
            hours: true,
            minutes: true,
            seconds: false
          }
        }
      });

      // Subscribes to the change event of the custom time picker
      __this.customTimePicker.subscribe(tempusDominus.Namespace.events.change, (e) => {
        // Formats the last picked time and updates the custom time input and button text
        const time = __this.customTimePicker.dates.lastPicked.format('h:mm t');
        __this.fcCustomTime.val(moment(__this.customTimePicker.dates.lastPicked).toISOString());
        __this.btnCustomTime.text(time);
      });

      // Initializes input spinner for numeric inputs
      $(".input-spinner-wrap input").inputSpinner({buttonsOnly: true});

      // Days validation
      __this.inputDays.on('change', function () {
        const $this = $(this);
        Utils.validateMultiCheckbox($this);
      });

      /**
       * Handles changes on time selection inputs to manage the state of "Anytime" options versus specific times,
       * and controls the visibility and state of a custom time switch and associated form inputs.
       * This functionality is encapsulated within an object method, assuming `this` refers to a specific instance with defined properties.
       */
      __this.inputTime.on('change', function () {
        const $this = $(this);
        const form = $this.closest('form.needs-validation');
        const name = $this.attr('name');
        const $inputsWithoutAnytime = $(`:input[name="${name}"]:not([value="Anytime"])`, form);
        const $inputAnytime = $(`:input[name="${name}"][value="Anytime"]`, form);

        if ($this.val() === 'Anytime') {
          if ($this.is(":checked")) {
            $inputsWithoutAnytime.each(function () {
              $(this).prop('checked', false);
            });
          }
        } else {
          $inputAnytime.prop('checked', false);
        }

        const $switch = $('#switchCustomTime', __this.$form);
        self.setSwitchVal($switch, false);

        resetCustomTime();

        Utils.validateMultiCheckbox($this);
      });

      const resetCustomTime = function () {
        __this.fcCustomTime.removeAttr('required');
        __this.fcCustomTime.val('').removeClass('is-valid');
        __this.btnCustomTime.text('');
      }
      /**
       * Handles changes on a custom time switch to manage the requirement state of associated inputs
       * and clear selections among other time options.
       */
      $('#switchCustomTime', __this.$form).on('change', function () {
        const $this = $(this); // Capture the jQuery object of the changed switch.
        const form = $this.closest('form.needs-validation'); // Find the closest parent form that requires validation.
        // Select the collapsible content associated with the custom time switch.
        let $collapse = $this.attr('data-bs-target');
        $collapse = $($collapse);
        // Find the specific input within the collapsible content meant for custom time input.
        const $input = $('input#customTimeInput', $collapse);
        const name = $input.attr('name'); // Get the name attribute of this input for group identification.
        // Select all checkbox inputs with the same name within the form.
        const $inputs = $(`input[name="${name}"][type="checkbox"]`, form);

        // Uncheck all checkboxes and adjust their 'required' attribute accordingly based on the switch's state.
        $inputs.each(function () {
          $(this).prop('checked', false).removeAttr('required').removeClass('is-valid');
        });

        if ($this.is(":checked")) {
          $input.attr('required', "required");
        } else {
          resetCustomTime();
          $inputs.attr('required', 'required');
        }
      });

      /**
       * Handles changes on a tracking ID switch to toggle the 'required' attribute of associated inputs,
       * facilitating conditional form validation based on user selection.
       */
      $('#switchTrackingId', __this.$form).on('change', function () {
        const $this = $(this); // Capture the jQuery object of the toggled switch.
        const form = $this.closest('form.needs-validation'); // Find the closest parent form that requires validation.
        // Select the collapsible content associated with the tracking ID switch.
        let $collapse = $this.attr('data-bs-target');
        $collapse = $($collapse);
        // Find the input within the collapsible content for tracking ID.
        const $input = $('input', $collapse);

        // Toggle the 'required' attribute of the input based on the switch's state.
        if ($this.is(":checked")) {
          $input.attr('required', "required");
        } else {
          $input.removeAttr('required').val('');
        }
      });

      // Initializes scrollbar for the specified element
      new SimpleBar(document.getElementById('tab_block_1'), {autoHide: true});

      // Replaces feather icons
      feather.replace();
    },

    /**
     * Sets the value of a switch input and optionally triggers a change event. Additionally, it manages
     * the visibility of a collapsible element associated with the switch based on the switch's value.
     *
     * @param {jQuery} $switch - The jQuery object representing the switch input whose value is to be set.
     * @param {boolean} value - The value to set on the switch input (true for checked, false for unchecked).
     * @param {boolean} triggerChange - Flag indicating whether a change event should be triggered on the switch after setting its value.
     */
    setSwitchVal: function ($switch, value, triggerChange = false) {
      // Set the switch's checked property based on the provided value.
      $switch.prop('checked', value);

      // If triggerChange is true, trigger the 'change' event on the switch.
      if (triggerChange) {
        $switch.trigger('change');
      }

      // Retrieve the data-bs-target attribute from __this.switchCustomTime to identify the collapsible element.
      const collapseTarget = $switch.attr('data-bs-target');

      // If collapseTarget is defined, manage the visibility of the associated collapsible element.
      if (typeof collapseTarget !== 'undefined') {
        // Create a new instance of bootstrap.Collapse for the target element, or get the existing one.
        const collapse = bootstrap.Collapse.getOrCreateInstance(collapseTarget, {
          toggle: false // Initialize without toggling the current state.
        });

        // Show or hide the collapsible element based on the value parameter.
        if (value) {
          collapse.show();
        } else {
          collapse.hide();
        }
      }
    },

    // Defines the setValues function responsible for updating UI elements based on the provided feed settings.
    setValues: function (values) {
      try {
        // Assign the provided values to feedSettings for clarity and further manipulation.
        const feedSettings = values;

        // Iterate over each day input element to check/uncheck based on the feedSettings.
        __this.inputDays.each(function () {
          const $this = $(this);
          // If the current day is included in the feedSettings' custom days, check the box and trigger a change event.
          if (feedSettings.timeFrames.custom.days.includes($this.val())) {
            $this.prop('checked', true).trigger('change');
          }
        });

        // Handle custom time settings. Check if the time value doesn't include 'Z' (not in ISO format) or is an object.
        if (
            feedSettings.timeFrames.custom.time[0].indexOf('Z') === -1 ||
            typeof feedSettings.timeFrames.custom.time[0] === 'object'
        ) {
          // Cleans the time array by removing any empty or null values.
          feedSettings.timeFrames.custom.time = app.removeEmptyValues(feedSettings.timeFrames.custom.time);
          // Iterate over each time input element to check/uncheck based on the cleaned feedSettings.
          __this.inputTime.each(function () {
            const $this = $(this);
            if (feedSettings.timeFrames.custom.time.includes($this.val())) {
              $this.prop('checked', true).trigger('change');
            }
          });
        } else {
          // If the time is in ISO format, parse it, convert to Date object, set the custom time picker value, and enable the switch.
          let customTime = moment(feedSettings.timeFrames.custom.time[0]);
          customTime = customTime.toDate();
          customTime = __this.customTimePicker.dates.parseInput(customTime);
          __this.customTimePicker.dates.setValue(customTime);
          this.setSwitchVal(__this.switchCustomTime, true, true);
        }

        // Set the frequency of post times input value based on feedSettings.
        __this.inputFrequencyPostTimes.val(feedSettings.frequency.postTimes);

        // If the mdcId is not empty, set its input value and enable the associated switch.
        if (!app.isEmpty(feedSettings.mdcId)) {
          __this.inputMdcId.val(feedSettings.mdcId);
          this.setSwitchVal(__this.switchInputMdcId, true, true);
        }

        // If appending description is enabled in feedSettings, check the associated switch.
        if (!app.isEmpty(feedSettings.appendDescription)) {
          __this.switchAppendArticleDescription.prop('checked', true);
        }

        // Always enable the randomly post switch; may adjust based on additional logic not shown here.
        __this.switchRandomly.prop('checked', true);

        // If chronic posting is enabled in feedSettings, check the associated switch.
        if (!app.isEmpty(feedSettings.chronicPost)) {
          __this.switchChronic.prop('checked', true);
        }
      } catch (e) {
        // Log any errors that occur during the execution of the function.
        console.log(e);
      }
    },
  }

  class FeedList extends TableList {
    /**
     * Constructs an instance of the CampaignList class.
     */
    constructor(app, container, listRow, searchInputId = 'searchTable') {
      super(app, container, listRow, searchInputId)
    }

    prepareRow(row) {
      const page = _.template(this.app.templates[this.listRow]);

      const defaults = {'count': ''};
      row = {...defaults, ...row};

      let pageData = {
        ...row,
        status: app.isEmpty(row.last_updated) ? 'pending' : 'active'
      };

      const $page = $(page(pageData));

      return $page;
    }

    async renderList() {
      const self = this;
      const $listContainer = this.container
      const app = this.app;
      $listContainer.empty();

      this.showLoader({
        id: null,
        name: this.staticPH,
        status: this.staticPH,
        count: this.staticPH,
        display: null
      });

      try {

        const response = await app.apis['feed'].fetch();
        $listContainer.empty();
        NoRecordsPlaceholder.destroy();

        if (typeof response.data != 'undefined' && response.data.length) {
          _.each(response.data, function (row) {
            __this.feeds[row.id] = row;
            let $row = self.prepareRow(row);
            $row.appendTo($listContainer);
          });
        } else {
          const noRecordsOptions = {
            title: 'No Feeds Found',
            class: 'available-table-height',
            description: 'You can start creating a new feed by clicking the button below',
            addButton: {
              text: 'Add New',
              onClick: (e) => {
                $(e.currentTarget).attr('data-feed-action', 'new-feed');
                feedAction = feedAction.bind($(e.currentTarget));
                feedAction(e);
              }
            },
            help: {
              text: 'Need Help ?',
              onClick: () => {
                console.log('Help clicked');
                // Code to open help documentation or contact support
              }
            }
          };

          const placeholder = new NoRecordsPlaceholder(app, this.container.closest('.card-body'), noRecordsOptions);
          placeholder.render();
        }
      } catch (error) {
        console.error("Error occurred during operation:", error);
      }
    }
  }

  function feedAction(e) {
    e.preventDefault();
    e.stopPropagation();

    const app = PreBuilt;
    const $this = $(this);
    const action = $this.data('feed-action');
    const $item = $(e.target).closest('tr.feed-row');
    let feedId = $item.data('id');

    if (action === 'new-feed') {
      let options = {
        text: '',
        twitter: '',
        type: 'input',
        modalDialogClass: 'modal-xl modal-dialog-centered modal-dialog-scrollable  modal-add-feeds',
        template: 'feed__new-feed',
        title: 'New RSS Feed',
        saveButtonText: 'Add to Feeds Library',
        footerText: '<span class="fw-semibold"><span class="text-primary"> Note: </span>Select multiple feeds by clicking on them</span>',
        payloadProcessor: 'NewFeed.payloadProcessor',
        saveApi: 'feed.addLibraryFeed',
        saveApiCallBack: 'NewFeed.saveCallback',
        placeholderCustomCollapseId: app.generateId(),
        placeholderSystemCollapseId: app.generateId()
      }

      const page = _.template(app.templates['global__checkbox-switch']);

      options.placeholderCustom = page({
        label: 'Custom RSS Feed',
        id: 'custom-feed',
        collapse: `#${options.placeholderCustomCollapseId}`,
        name: 'custom-feed',
      });
      options.placeholderSystem = page({
        label: 'Select Feed from library',
        id: 'system-feed',
        collapse: `#${options.placeholderSystemCollapseId}`,
        name: 'system-feed',
      });

      app.inputPrompt(options).then(async function ($modal) {
        app.newFeedModal = $modal;

        const systemFeed = $modal.find(':input[name=system-feed]');
        Utils.setSwitchVal(systemFeed, true, true);

        const $systemFeedsContainer = $modal.find('[data-system-feeds]');
        $systemFeedsContainer.addClass('pt-10 pb-10');
        const progress = new ProgressBar($modal.find('.container-fluid'));
        progress.start();

        const systemFeeds = await app.apis['feed'].fetchSystemFeeds();

        $systemFeedsContainer.removeClass('pt-10 pb-10');

        const page = _.template(app.templates['feed__feeds']);
        const $page = $(page({feedGroups: systemFeeds.data}));
        $systemFeedsContainer.append($page);
        progress.remove();

      });
    }

    if (action === 'new-custom') {
      let options = {
        text: '',
        twitter: '',
        type: 'input',
        modalDialogClass: 'modal-lg  modal-add-feeds',
        template: 'feed__custom-feed',
        title: 'New Custom RSS Feed',
        saveButtonText: 'Add Custom Feed',
        footerText: '<span class="fw-semibold"><span class="text-primary"> Note: </span>Once a feed is added, it can take up to 15 min before articles are available to post.</span>',
        payloadProcessor: 'NewFeed.payloadProcessor',
        saveApi: 'feed.addCustomFeed',
        saveApiCallBack: 'NewFeed.saveCustomCallback'
      }

      app.inputPrompt(options).then(async function ($modal) {
        app.newCustomFeedModal = $modal;

      });
    }

    if (action === 'delete') {
      app.confirm("Delete RSS Feed?", "Are you sure you want to delete this Rss Feed.").then(function (response) {
        const $button = response.button;
        const confirm = response.confirm;
        const $modal = response.modal;

        // If the user confirms the deletion, proceed with the delete operation.
        if (confirm === true) {
          app.doButtonAction($button);
          app.apis['feed'].deleteFeed(feedId).done(function (response) {
            if (response.success !== true) {

            } else {
              __this.listMananger.renderList();
            }
            app.doButtonReset($button, true, 100);
            app.hideModal($modal);
          }).fail(function (response) {
            app.doButtonReset($button, false);
            app.hideModal($modal);
          });
        }
      });
    }

  }

  function feedChangeAction(e) {
    const app = PreBuilt;
    const $this = $(this);
    const action = $this.data('feed-change-action');
    const $item = $(e.target).closest('tr.feed-row');
    let feedId = $item.data('id');

    if(action === 'remove-feed') {
      const $button = $this;
      const $switch = $button.closest('.form-switch');
      $switch.css({'margin-left': '-4.5rem'});
      app.doButtonAction($button);
      app.apis['feed'].update(feedId, {display : $this.is(":checked") }).done(function (response) {
        if (response.success !== true) {

        } else {
          __this.listMananger.renderList();
        }

        $switch.css({'margin-left': '0'});
        app.doButtonReset($button, true, 100);
      }).fail(function (response) {
        app.doButtonReset($button, false);
      });
    }
    //console.log(F)
  }
  class NewFeed extends BaseClass {
    constructor(app) {
      super(app);
      this.isRendering = true;
      this.app = app;
      this.observerManager = new ImageObserverManager(this.app, null, null);
    }

    static payloadProcessor(requestData) {
      return requestData;
    }

    static saveCallback(response) {
      const app = PreBuilt;
      app.notifyEl.update({'message': response.message, progress: 90});
      app.newFeedModal.modal('hide');
      __this.listMananger.renderList();
    }

    static saveCustomCallback(response) {
      const app = PreBuilt;
      app.notifyEl.update({'message': response.message, progress: 80});
      app.newCustomFeedModal.modal('hide');
      __this.listMananger.renderList();
    }

  }

  // Creating a map of class references
  window["NewFeed"] = NewFeed;

  /**
   * The list component responsible for managing and rendering the list of feeds.
   */
  aimi_feed.list = {
    listRow: 'feed__row-feed',

    init: function (calendarId) {
      __this.calendarId = calendarId;
      const $listContainer = $('[data-article-feeds]');

      __this.listMananger = new FeedList(app, $listContainer, this.listRow, 'searchTable');
      __this.listMananger.renderList();

      const feedsEventManager = new EventManager('feeds', '[data-feed-action]');
      feedsEventManager.addEventListener('click', feedAction);

      const feedsChangeEventManager = new EventManager('feeds', '[data-feed-change-action]');
      feedsChangeEventManager.addEventListener('change', feedChangeAction);

      app.initSimpleBar(document.getElementById('tab_block_1'));
    }
  }

  aimi_feed.updateCallBack = function (response) {
    response.message = 'feed saved successfully';
    app.notifyEl.update({'message': response.message, progress: 90});
    aimi_feed.list.renderList().then(function () {
      aimi_feed.view.setValues(__this.feeds[response.id]);
    });
  }

}(window.aimi_feed = window.aimi_feed || {}, jQuery, PreBuilt));
